/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

// --- Styles ---
import './App.scss'; // Import global styles (including CSS vars)

// --- Contexts ---
import { AuthProvider, useAuth } from './contexts/AuthContext'; // Adjust path as needed
import UiLanguageSync from './i18n/UiLanguageSync';
import { RolePlayProvider } from './contexts/RolePlayContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { SceneAssetProvider } from './contexts/SceneAssetContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AssignmentCountProvider } from './contexts/AssignmentCountContext';
import { PolicyProvider } from './contexts/PolicyContext';
import { TutorialProvider } from './components/tutorial/TutorialContext';

// --- Layout Components ---
import Layout from './components/layout/Layout'; // Adjust path

// --- Page Components ---
// Only the pages needed to render a first frame on any route are imported
// eagerly: the layout/guard chrome, the auth pages that the guards below render
// directly (outside the Routes' Suspense boundary), and the error pages, which
// must not need a network fetch to display. Everything else is code-split by
// React.lazy so a route pays only for its own chunk — before this, three.js,
// drei, @google/genai and chart.js all sat in the main bundle and were parsed
// on every page load, including the plain tables.
import Login from './components/auth/Login'; // Assuming you have a Login page component
import FTUEGuard from './components/ftue/FTUEGuard'; // FTUE Guard for routing
import PolicyGuard from './components/policy/PolicyGuard'; // EULA/PP acceptance guard
import PolicyDocumentPage from './components/policy/PolicyDocumentPage'; // Public read-only PP/EULA pages
import { POLICY_PAGE_PATHS, isPolicyPagePath } from './components/policy/policyPaths';
import JoinRequestGuard from './components/join-requests/JoinRequestGuard'; // blocks applicants whose join request denies access
import JoinRequestsPage from './components/join-requests/JoinRequestsPage';
import ClassesPage from './components/classes/ClassesPage';
import ClassDetailPage from './components/classes/ClassDetailPage';
import MyClassesPage from './components/classes/MyClassesPage';
import StudentHomePage from './components/classes/StudentHomePage';
import StudentGradesPage from './components/classes/StudentGradesPage';
import AttemptReviewPage from './components/classes/AttemptReviewPage';
import Signup from './components/auth/Signup'; // Self-service signup (also rendered by OrganizationGuard)
import SessionExpired from './components/auth/SessionExpired'; // Session expired page
import NoOrganizationPage from './components/auth/NoOrganizationPage'; // No org assigned page (rendered by OrganizationGuard)
import SubscriptionCancelledPage from './components/auth/SubscriptionCancelledPage'; // Rendered by SubscriptionGuard

// --- Error pages ---
import NotFoundPage from './components/error-pages/NotFoundPage';
import ServerErrorPage from './components/error-pages/ServerErrorPage';
import ServiceUnavailablePage from './components/error-pages/ServiceUnavailablePage';
import AppErrorBoundary from './components/error-pages/AppErrorBoundary';

// --- API client navigator registration ---
import { setApiNavigator } from './utils/apiClient';

// --- Utilities & Types ---
import ProtectedRoute from './components/protected-route/ProtectedRoute'; // Adjust path
import { AppUser } from './types'; // Adjust path
import RoleProtectedRoute from './components/protected-route/RoleProtectedRoute';
import LoadingState from './components/shared/LoadingState';

// Google Analytics Import
import RouteTracker from './components/RouteTracker';
import { initGA } from './utils/analytics';
import { useNotificationPolling } from './hooks/use-notification-polling';

// --- Lazily-loaded route pages ---
// RoleplayRoute bundles GeminiDirectProvider with the page so the Gemini SDK and
// the 3D stack land in this chunk rather than the main one.
const RoleplayRoute = React.lazy(() => import('./components/avatar/RoleplayRoute'));
const ClassroomScenePage = React.lazy(() => import('./components/classroom-scene/ClassroomScenePage'));
const ForgotPassword = React.lazy(() => import('./components/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./components/auth/ResetPassword'));
const MagicLinkAuth = React.lazy(() => import('./components/auth/MagicLinkAuth'));
const MarketplaceSignup = React.lazy(() => import('./components/auth/MarketplaceSignup'));
const PricingPage = React.lazy(() => import('./components/auth/PricingPage'));
const SsoPage = React.lazy(() => import('./components/SsoRedirect'));
const FTUEFlow = React.lazy(() => import('./components/ftue/FTUEFlow'));
const DeviceSetupPage = React.lazy(() => import('./components/ftue/DeviceSetupPage'));
const SubscriptionPage = React.lazy(() => import('./components/subscription/SubscriptionPage'));
const PaymentSuccessPage = React.lazy(() => import('./components/trial/PaymentSuccessPage'));
const ProfilePage = React.lazy(() => import('./components/profile/ProfilePage'));
const HomePage = React.lazy(() => import('./components/home/HomePage'));
const RolePlayList = React.lazy(() => import('./components/role-play/RolePlayList'));
const CharactersPage = React.lazy(() => import('./components/characters/CharactersPage'));
const UsersPage = React.lazy(() => import('./components/users/UsersPage'));
const UserDetailPage = React.lazy(() => import('./components/users/UserDetailPage'));
const SessionsPage = React.lazy(() => import('./components/sessions/SessionsPage'));
const SessionDetailPage = React.lazy(() => import('./components/sessions/SessionDetailPage'));
const AnalyticsDashboard = React.lazy(() =>
    import('./components/dashboard/analytics').then(m => ({ default: m.AnalyticsDashboard })),
);
const UserReportPage = React.lazy(() => import('./components/dashboard/reports/UserReportPage'));
const OrganizationPage = React.lazy(() => import('./components/organization/OrganizationPage'));
const OrganizationDetailPage = React.lazy(() => import('./components/organization/OrganizationDetailPage'));
const MyOrganizationRedirect = React.lazy(() => import('./components/organization/MyOrganizationRedirect'));
const FeedbackManagementPage = React.lazy(() => import('./components/feedback-management/FeedbackManagementPage'));
const ReviewManagementPage = React.lazy(() => import('./components/reviews/ReviewManagementPage'));
const ReviewerDashboardPage = React.lazy(() => import('./components/reviews/ReviewerDashboardPage'));
const PromptStudioView = React.lazy(() => import('./components/PromptStudioView'));
const MyProgressPage = React.lazy(() => import('./components/gamification/MyProgressPage'));
const CompetitionsPage = React.lazy(() => import('./components/competition/CompetitionsPage'));
const TrainerRosterPage = React.lazy(() => import('./components/gamification/TrainerRosterPage'));
const PlatformDashboardPage = React.lazy(() => import('./components/gamification/PlatformDashboardPage'));
const MyAssignmentsPage = React.lazy(() => import('./components/assignments/MyAssignmentsPage'));
const AssignmentsPage = React.lazy(() => import('./components/assignments/AssignmentsPage'));
const AssignmentDetailPage = React.lazy(() => import('./components/assignments/AssignmentDetailPage'));
const AssignmentFormPage = React.lazy(() => import('./components/assignments/AssignmentFormPage'));
const BundlesPage = React.lazy(() => import('./components/assignments/BundlesPage'));
const BundleFormPage = React.lazy(() => import('./components/assignments/BundleFormPage'));



function NotificationPollingWatcher() {
    useNotificationPolling();
    return null;
}

// GCP Marketplace redirects buyers to the root URL with ?x-gcp-marketplace-token=...
// This interceptor catches that before ProtectedRoute can strip the token,
// and forwards to the dedicated /marketplace-signup handler.
function MarketplaceTokenInterceptor({ children }: Readonly<{ children: React.ReactNode }>) {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const token = params.get('x-gcp-marketplace-token');
    if (token && location.pathname !== '/marketplace-signup') {
        return <Navigate to={`/marketplace-signup${location.search}`} replace />;
    }
    return <>{children}</>;
}

function OrganizationGuard({ children }: Readonly<{ children: React.ReactNode }>) {
    const { noOrganization, loading, isMarketplaceUser } = useAuth();
    const location = useLocation();
    // Always let /marketplace-signup through. A buyer finishing "Continue with Google"
    // is authenticated but legitimately has no org yet — they're about to create or pick
    // one on the finish-setup form (register-google assigns it). Blocking here would
    // strand them on "No Organization" mid-signup. Mirrors SubscriptionGuard's exemption.
    const isMarketplaceSignup = location.pathname === '/marketplace-signup';
    // The SSO finish-setup form links to the policy pages in a new tab; let those render.
    if (!loading && noOrganization && !isMarketplaceSignup && !isPolicyPagePath(location.pathname)) {
        // A plain SSO login (not a marketplace buyer) with no org yet — let them
        // self-serve org/profile setup instead of the marketplace's "No Organization"
        // contact-admin message.
        if (!isMarketplaceUser) return <Signup ssoCompletion />;
        return <NoOrganizationPage />;
    }
    return <>{children}</>;
}

function SubscriptionGuard({ children }: Readonly<{ children: React.ReactNode }>) {
    const { subscriptionCancelled, loading } = useAuth();
    const location = useLocation();
    // Always let /marketplace-signup through so the GCP JWT can be processed and the
    // claim gate can run — blocking it here would strand re-subscribers permanently.
    const isMarketplaceSignup = location.pathname === '/marketplace-signup';
    // The policy pages are public and read-only, so they render for every user.
    if (!loading && subscriptionCancelled && !isMarketplaceSignup && !isPolicyPagePath(location.pathname)) {
        return <SubscriptionCancelledPage />;
    }
    return <>{children}</>;
}

function AppRoutes() {
    const { currentUser } = useAuth() as { currentUser: AppUser | null };
    const navigate = useNavigate();

    // Register navigate with the API client so network/server errors auto-redirect
    useEffect(() => {
        setApiNavigator(navigate);
    }, [navigate]);

    useEffect(() => {
        const userId = currentUser?.id ?? 'guest';

        import('./utils/analytics').then(({ setUserId }) => {
            setUserId(userId);
        });
    }, [currentUser]);

    return (
        <>
            <RouteTracker /> {/* This line tracks route changes */}
            <NotificationPollingWatcher />
            <MarketplaceTokenInterceptor>
            <PolicyGuard>
            <FTUEGuard>
            {/* Innermost of the guards: an applicant blocked by a join request has still
                accepted the policy and finished device setup, so those guards must run
                first — otherwise the wall would hide flows they still need to complete. */}
            <JoinRequestGuard>
            {/* One boundary for every lazily-loaded route page. The guards above
                render eagerly-imported components, so they never suspend here. */}
            <Suspense fallback={<LoadingState fullScreen message="Loading..." />}>
            <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/sso" element={<SsoPage />} />
                <Route path="/auth/magic/:token" element={<MagicLinkAuth />} />
                <Route path="/marketplace-signup" element={<MarketplaceSignup />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/session-expired" element={<SessionExpired />} />
                <Route path={POLICY_PAGE_PATHS.privacy} element={<PolicyDocumentPage doc="privacy" />} />
                <Route path={POLICY_PAGE_PATHS.eula} element={<PolicyDocumentPage doc="eula" />} />
                {/* FTUE route (auth handled by FTUEGuard) - No Sidebar but keep TopHeader */}
                <Route path="/getting-started" element={
                    <Layout hideSidebar={true}>
                        <FTUEFlow />
                    </Layout>
                } />
                <Route
                    path="/"
                    element={
                        <ProtectedRoute>
                            <Layout>
                                <HomePage />
                            </Layout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/roleplay"
                    element={
                        <ProtectedRoute>
                            <Layout>
                                <RoleplayRoute />
                            </Layout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/payment-success"
                    element={
                        <ProtectedRoute>
                            <PaymentSuccessPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/sessions"
                    element={
                        <ProtectedRoute>
                            <Layout>
                                <SessionsPage />
                            </Layout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/sessions/:sessionId"
                    element={
                        <ProtectedRoute>
                            <Layout>
                                <SessionDetailPage />
                            </Layout>
                        </ProtectedRoute>
                    }
                />

                {/* Role-protected routes */}
                <Route
                    path="/role-plays"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'trainer']}>
                            <Layout>
                                <RolePlayList />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/characters"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <CharactersPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/users"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'trainer']}>
                            <Layout>
                                <UsersPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/users/:userId"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'trainer']}>
                            <Layout>
                                <UserDetailPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/dashboard"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'trainer']}>
                            <Layout>
                                <AnalyticsDashboard />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/reports"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin']}>
                            <Layout>
                                <UserReportPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/join-requests"
                    element={
                        // Not super admin: they have no org, so this page would only point them to
                        // Organizations — each org's queue lives on its detail page. Matches SideBar.
                        <RoleProtectedRoute allowedRoles={['admin', 'trainer']}>
                            <Layout>
                                <JoinRequestsPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/classes"
                    element={
                        // Student system. The server is the gate: an admin whose school has it
                        // off gets an empty page with the switch to turn it on.
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <ClassesPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/classes/:classId"
                    element={
                        // Any role: teachers and students are class memberships, not app roles,
                        // so the server decides per class who may open it.
                        <ProtectedRoute>
                            <Layout>
                                <ClassDetailPage />
                            </Layout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/classes/:classId/attempts/:attemptId"
                    element={
                        // Teachers and grantees are class memberships, not app roles: the
                        // server decides per class who may review and grade.
                        <ProtectedRoute>
                            <Layout>
                                <AttemptReviewPage />
                            </Layout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/my-grades"
                    element={
                        <ProtectedRoute>
                            <Layout>
                                <StudentGradesPage />
                            </Layout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/my-classes"
                    element={
                        <ProtectedRoute>
                            <Layout>
                                <MyClassesPage />
                            </Layout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/my-practice"
                    element={
                        <ProtectedRoute>
                            <Layout>
                                <StudentHomePage />
                            </Layout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/organizations"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin']}>
                            <Layout>
                                <OrganizationPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/organizations/:orgId"
                    element={
                        // Admins too: their own org and its departments (the server decides
                        // which — see app/user/org_hierarchy.py).
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <OrganizationDetailPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/my-organization"
                    element={
                        <RoleProtectedRoute allowedRoles={['admin']}>
                            <MyOrganizationRedirect />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/feedback"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <FeedbackManagementPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/reviews"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'trainer']}>
                            <Layout>
                                <ReviewManagementPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/reviewer-dashboard"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'trainer']}>
                            <Layout>
                                <ReviewerDashboardPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/prompt-studio"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <PromptStudioView />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/prompt-studio/prompts/new"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <PromptStudioView />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/prompt-studio/prompts/:id"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <PromptStudioView />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/prompt-studio/templates/new"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <PromptStudioView />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/prompt-studio/templates/:id"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <PromptStudioView />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/prompt-studio/content-blocks/new"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <PromptStudioView />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/prompt-studio/content-blocks/:id"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <PromptStudioView />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/prompt-studio/evaluations/new"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <PromptStudioView />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/prompt-studio/evaluations/:id"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <PromptStudioView />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />

                {/* Assignment Engine routes */}
                <Route
                    path="/my-assignments"
                    element={
                        <RoleProtectedRoute allowedRoles={['admin', 'trainer', 'trainee']} blockTrial>
                            <Layout>
                                <MyAssignmentsPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/assignments"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'trainer']}>
                            <Layout>
                                <AssignmentsPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/assignments/new"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <AssignmentFormPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/assignments/:id"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'trainer']}>
                            <Layout>
                                <AssignmentDetailPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/assignments/:id/edit"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <AssignmentFormPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/bundles"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <BundlesPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/bundles/new"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <BundleFormPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/bundles/:id/edit"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <BundleFormPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />

                {/* Redirects for old routes */}
                <Route path="/templates" element={<Navigate to="/prompt-studio?tab=templates" replace />} />
                <Route path="/content-blocks" element={<Navigate to="/prompt-studio?tab=content-blocks" replace />} />
                <Route path="/prompt-configs" element={<Navigate to="/prompt-studio?tab=prompts" replace />} />
                <Route path="/evaluation-configs" element={<Navigate to="/prompt-studio?tab=prompts" replace />} />

                <Route
                    path="/profile"
                    element={
                        <ProtectedRoute>
                            <Layout>
                                <ProfilePage />
                            </Layout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/device-setup"
                    element={
                        <ProtectedRoute>
                            <Layout>
                                <DeviceSetupPage />
                            </Layout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/subscription"
                    element={
                        <RoleProtectedRoute allowedRoles={['admin', 'trainer']}>
                            <Layout>
                                <SubscriptionPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/scene-editor"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                            <Layout>
                                <ClassroomScenePage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />

                {/* Error pages — accessible directly or via redirect */}
                <Route path="/error/404" element={<NotFoundPage />} />
                <Route path="/error/500" element={<ServerErrorPage />} />
                <Route path="/error/503" element={<ServiceUnavailablePage />} />

                <Route
                    path="/my-progress"
                    element={
                        <ProtectedRoute blockTrial>
                            <Layout>
                                <MyProgressPage />
                            </Layout>
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/competitions"
                    element={
                        <ProtectedRoute blockTrial>
                            <Layout>
                                <CompetitionsPage />
                            </Layout>
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/team-progress"
                    element={
                        <RoleProtectedRoute allowedRoles={['admin', 'trainer']}>
                            <Layout>
                                <TrainerRosterPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />

                <Route
                    path="/platform-dashboard"
                    element={
                        <RoleProtectedRoute allowedRoles={['super_admin']}>
                            <Layout>
                                <PlatformDashboardPage />
                            </Layout>
                        </RoleProtectedRoute>
                    }
                />

                {/* Catch-all: unknown routes → 404 error page */}
                <Route path="*" element={<Navigate to="/error/404" replace />} />
            </Routes>
            </Suspense>
            </JoinRequestGuard>
            </FTUEGuard>
            </PolicyGuard>
            </MarketplaceTokenInterceptor>
        </>
    );
}

function App() {
    // Initialize GA when app loads
    useEffect(() => {
        initGA();
    }, []);

    return (
        <AppErrorBoundary>
            <BrowserRouter>
                <AuthProvider>
                    <UiLanguageSync />
                    <PolicyProvider>
                        <NotificationProvider>
                            <AssignmentCountProvider>
                            <SubscriptionGuard>
                            <OrganizationGuard>
                                <OrganizationProvider>
                                    <RolePlayProvider>
                                        <SceneAssetProvider>
                                            <TutorialProvider>
                                                <AppRoutes />
                                            </TutorialProvider>
                                        </SceneAssetProvider>
                                    </RolePlayProvider>
                                </OrganizationProvider>
                            </OrganizationGuard>
                            </SubscriptionGuard>
                            </AssignmentCountProvider>
                        </NotificationProvider>
                    </PolicyProvider>
                </AuthProvider>
            </BrowserRouter>
        </AppErrorBoundary>
    );
}

export default App;